import {FormikProvider, useFormik} from 'formik';
import {domToReact, Element as ParserElement, HTMLReactParserOptions, DOMNode} from 'html-react-parser';
import {isEmpty, map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {sessionApi} from 'api';
import {Button, ClientDynamicContent, FormGroup, Icon, InputFormik, Loader, NodeElement} from 'components';
import {DynamicComponentNames, JoinSessionUnits as Units, JOIN_SESSION_DEFAULT_TEMPLATE} from 'constants/client';
import {useClientSession} from 'hooks';
import {ClientDevice, JoinClientSessionParams} from 'interfaces/client';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeToken} from 'services';
import {ApiError, InviteeParticipantRole} from 'types';
import {dynamicContentUtils, apiUtils, platformUtils} from 'utils';

import {ClientConferenceWeb} from '../ClientConferenceWeb';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.CLIENT_JOIN_SESSION_PAGE;

const CODE_LENGTH = 6;
const PIN_LENGTH = 4;
const INVALID_FIELD_PLACEHOLDER = 'INVALID';

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        title, description, error, errorMessages, renderForm, renderNotStyledInput, renderSubmit, handleSubmit, submitDisabled,
        role
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.Icon, node)) {
                return (
                    <NodeElement node={node}>
                        <Icon
                            name='lock-closed'
                            size='initial'
                            color='primary-light'
                            className='icon'
                        />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{title}</NodeElement>;
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node} show={!error}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.Errors, node)) {
                return (
                    <NodeElement node={node} show={!!error}>
                        {map(errorMessages, (errorMessage, index) =>
                            <div key={index} className='errors'>{errorMessage}</div>)}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.FormDefault, node)) {
                return <NodeElement node={node}>{renderForm()}</NodeElement>;
            }
            if (isMatchElement(Units.CodeInputContainer, node)) {
                return renderNotStyledInput(node, 'code');
            }
            if (isMatchElement(Units.PinInputContainer, node)) {
                return renderNotStyledInput(node, 'pin');
            }
            if (isMatchElement(Units.SubmitButton, node)) {
                return (
                    <NodeElement node={node} disabled={submitDisabled} onClick={handleSubmit}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.SubmitButtonDefault, node)) {
                return <NodeElement node={node}>{renderSubmit()}</NodeElement>;
            }
            return renderDefaultNode(node, {role});
        }
    };
};

type FormFields = {
    code: string;
    pin: string;
};

const validationSchema = Yup.object().shape({
    code: Yup.string().required().length(CODE_LENGTH),
    pin: Yup.string().required().length(PIN_LENGTH)
});

type Props = {
    match: FormFields;
    role?: InviteeParticipantRole;
    onSubmit: (values: FormFields) => void;
}

export const ClientJoinSession: React.FC<Props> = (({match, role, onSubmit}: Props) => {
    const {t} = useTranslation();
    const [error, setError] = useState<ApiError>(null);
    const [token, setToken] = useState<string>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const {image} = useClientSession();

    const hasJoinParamsInUrl = useMemo(() => {
        const {code, pin} = match;
        return !!(code && pin);
    }, [match]);

    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(hasJoinParamsInUrl);

    const errorMessages = useMemo(() => {
        if (!error) {
            return null;
        }
        if (apiUtils.isNotFoundError(error)) {
            return [t(T_KEYS.ERRORS.NOT_FOUND), t(T_KEYS.ERRORS.CHECK_PIN_CODE)];
        }
        return [t(T_KEYS.ERRORS.UNEXPECTED), t(T_KEYS.ERRORS.TRY_LATER)];
    }, [error, t]);

    const joinSession = useCallback(async (code: string, pin: string) => {
        setError(null);
        setIsLoading(true);
        try {
            const device = ClientDevice.Browser;
            const params: JoinClientSessionParams = {
                inviteClientParams: {
                    code: +code,
                    pin: +pin
                },
                data: {
                    device,
                    clientInfo: platformUtils.getPlatformInfo()
                },
                role
            };
            const {token: clientToken} = await sessionApi.joinAsClient(params);
            InviteeToken.set(clientToken);
            setToken(clientToken);
        } catch (e: any) {
            setError(e);
        } finally {
            setIsLoading(false);
            setIsInitialLoading(false);
        }
    }, [role]);

    const formik = useFormik({
        validationSchema,
        initialValues: {
            code: hasJoinParamsInUrl ? match.code : '',
            pin: hasJoinParamsInUrl ? match.pin : ''
        },
        onSubmit: (formValues: FormFields) => {
            onSubmit(formValues);
        }
    });

    const {values, errors, submitForm, setErrors} = formik;

    const submitDisabled = useMemo(() => isLoading || !isEmpty(errors) || !values.code || !values.code, [errors, isLoading, values.code]);

    const renderInput = useCallback((name: string, styled = true) => {
        if (name === 'code') {
            return (
                <InputFormik
                    name='code'
                    required
                    disabled={isLoading}
                    skipErrorMessage
                    styled={styled}
                    label={t(T_KEYS.CODE)}
                />
            );
        } else if (name === 'pin') {
            return (
                <InputFormik
                    name='pin'
                    required
                    disabled={isLoading}
                    skipErrorMessage
                    styled={styled}
                    label={t(T_KEYS.PIN)}
                />);
        }
    }, [t, isLoading]);

    const renderNotStyledInput = useCallback((node: ParserElement, name: string) =>
        <NodeElement node={node}>{renderInput(name, false)}</NodeElement>, [renderInput]);

    const renderSubmit = useCallback(() => (
        <Button
            width='full'
            height='full'
            color='primary'
            className='submit'
            disabled={submitDisabled}
            testAttribute={{id: 'join-session-button'}}
            onClick={submitForm}
        >
            {t(T_KEYS.SUBMIT_BTN)}
        </Button>
    ), [t, submitDisabled, submitForm]);

    const renderForm = useCallback(() => (
        <form className='controls' onSubmit={submitForm}>
            <FormGroup>{renderInput('code')}</FormGroup>
            <FormGroup>{renderInput('pin')}</FormGroup>
            <div className='actions'>
                {renderSubmit()}
            </div>
            {isLoading && <div className='loader'><Loader svgClassName='tw-max-w-12 tw-max-h-12' /></div>}
        </form>
    ), [isLoading, renderInput, renderSubmit, submitForm]);

    useEffect(() => {
        if (error) {
            setErrors({
                code: INVALID_FIELD_PLACEHOLDER,
                pin: INVALID_FIELD_PLACEHOLDER
            });
        }
    }, [error, setErrors]);

    useEffect(() => {
        if (hasJoinParamsInUrl) {
            setIsInitialLoading(true);
            joinSession(match.code, match.pin);
        }
    }, [match, joinSession, setIsInitialLoading, hasJoinParamsInUrl]);

    if (isInitialLoading) {
        return <Loader svgClassName='tw-max-w-12 tw-max-h-12' />;
    }
    if (token) {
        return <ClientConferenceWeb />;
    }

    return (
        <FormikProvider value={formik}>
            <ClientDynamicLayout image={image}>
                <ClientDynamicContent
                    component={DynamicComponentNames.JoinSession}
                    defaultTemplate={JOIN_SESSION_DEFAULT_TEMPLATE}
                    optionsProps={{
                        title: t(T_KEYS.TITLE),
                        description: t(T_KEYS.DESCRIPTION),
                        error,
                        errorMessages,
                        submitDisabled,
                        role,
                        handleSubmit: submitForm,
                        renderForm,
                        renderNotStyledInput
                    }}
                    optionsGetter={getOptions}
                />
            </ClientDynamicLayout>
        </FormikProvider>
    );
});
